// =============================================================================
// Tenant-admin App Installs — a single tenant's PWA install adoption + nudge.
//
// Lives in the Platform tenant-manage console (/admin/tenants/:id/manage/
// app-installs). Reads only THIS tenant's numbers via the pwa-install-stats
// edge function in ?tenant_id mode (scoped server-side). Degrades to a quiet
// notice if the App DB is unreachable or migration 20261114 isn't applied.
// =============================================================================

import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import { fetchPwaInstallStats, type PwaInstallStats } from '@/lib/api';

const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

function StatTile({ label, value, sub, tone = 'default' }: {
  label: string; value: string; sub: string; tone?: 'default' | 'emerald' | 'amber' | 'muted';
}) {
  const valCol = tone === 'emerald' ? 'text-emerald-600'
    : tone === 'amber' ? 'text-amber-600'
    : tone === 'muted' ? 'text-surface-400'
    : 'text-surface-900';
  return (
    <div className="bg-surface-50 rounded-lg p-3">
      <p className="text-xs text-surface-500 mb-1">{label}</p>
      <p className={`text-xl font-medium ${valCol}`}>{value}</p>
      <p className="text-[11px] text-surface-400 mt-0.5">{sub}</p>
    </div>
  );
}

export function TenantAppInstallsPage({ tenantId }: { tenantId: string }) {
  const [stats, setStats] = useState<PwaInstallStats | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const s = await fetchPwaInstallStats({ tenantId });
    setStats(s); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-surface-900">App installs</h1>
          <p className="text-sm text-surface-500">How many of your users added the app to a device, and how the install nudge is converting. Excludes anonymous guests.</p>
        </div>
        <button onClick={load} disabled={loading} title="Refresh"
          className="inline-flex items-center justify-center rounded-lg border border-surface-200 p-2 text-surface-500 hover:bg-surface-50 hover:text-surface-900 disabled:opacity-50">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Spinner /></div>
      ) : !stats ? (
        <p className="text-sm text-surface-400">Install stats unavailable — App DB unreachable, or migration 20261114 not yet applied.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile label="Install rate" tone="emerald"
            value={`${pct(stats.installed, stats.total)}%`}
            sub={`${stats.installed.toLocaleString()} of ${stats.total.toLocaleString()} users`} />
          <StatTile label="Active installs · 30d"
            value={stats.activeStandalone30d.toLocaleString()}
            sub="opened the installed app" />
          <StatTile label="WhatsApp install rate"
            value={`${pct(stats.whatsappInstalled, stats.whatsappTotal)}%`}
            sub={`${stats.whatsappInstalled.toLocaleString()} of ${stats.whatsappTotal.toLocaleString()} WhatsApp users`} />
          <StatTile label="Nudged, not installed"
            tone={stats.nudgedCapped > 0 ? 'amber' : 'muted'}
            value={stats.nudgedCapped.toLocaleString()}
            sub={`hit 2-send cap · ${stats.nudgedAny.toLocaleString()} nudged total`} />
        </div>
      )}
    </div>
  );
}
